/**
 * Purpose: Owner-safe reservations list page displaying reservations for the selected property within the reporting period.
 * Provides KPI cards (reservations count, nights, revenue, inquiries, cancellations) and an interactive table
 * with search functionality. Excludes inquiry and cancelled reservations from the table (shown only in KPIs).
 * 
 * High-level Workflow:
 * 1. On mount, initializes reporting period to current month if not already set
 * 2. Fetches reservations from API when selected property or reporting period changes
 * 3. Maps API reservations to UI format and adds status bucket fields (booked, inquiry, cancelled)
 * 4. Filters reservations by reporting period date overlap (check_in < rangeEnd AND check_out > rangeStart)
 * 5. Excludes inquiry and cancelled reservations from table display (only shown in KPIs)
 * 6. Calculates KPIs: reservations count (excluding inquiry/cancelled), nights (booked only), revenue (booked only), inquiries count, cancellations count
 * 7. Renders KPI cards and reservations table with search functionality
 * 8. Handles booking detail modal on row click
 * 
 * Assumptions & Invariants:
 * - selectedProperty is provided by SessionContext (useSession hook)
 * - rangeStart and rangeEnd are provided by ReportingPeriodContext (useReportingPeriod hook)
 * - API returns owner-safe data (no guest contact details, no admin-only fields)
 * - All dates are handled in local timezone (no timezone shifts for date-only strings)
 * - Reservation overlap logic: reservation overlaps if check_in_date < rangeEnd AND check_out_date > rangeStart
 * - Table shows only BOOKED and OWNER_STAY reservations (inquiry and cancelled excluded)
 * - KPIs use booked reservations only for nights and revenue calculations
 * 
 * Dependencies:
 * Internal:
 *   - contexts/SessionContext.jsx - Authentication and property selection
 *   - contexts/ReportingPeriodContext.jsx - Reporting period management
 *   - components/owner/ReservationsTableEnhanced.jsx - Reservations table component
 *   - components/owner/BookingDetailModal.jsx - Booking details modal
 *   - components/shared/SkeletonLoader.jsx - Loading states
 *   - components/shared/ReportingPeriodBar.jsx - Reporting period UI
 *   - components/shared/OwnerModuleCard.jsx - Module card wrapper
 *   - components/owner/OwnerPageChrome.jsx - Page layout
 *   - components/shared/KPICardRow.jsx - KPI cards
 *   - services/ownerApi.js - API client
 *   - utils/sanitizeForOwnerView.js - Data sanitization
 *   - utils/hostawayStatus.js - Status utilities
 *   - utils/ownerReservationsMapper.js - Reservation mapping
 *   - utils/parseLocalDate.js - Date parsing
 *   - utils/reservationsDateUtils.js - Date range utilities
 *   - utils/reservationsFormatting.js - Formatting utilities
 * External:
 *   - react - React hooks (useState, useEffect, useMemo)
 * 
 * Version: 1.4.0
 * Created: 2026-01-26
 * Change Log:
 * - 2026-01-26: Phase 0, 1, 1.5 - Added charter-compliant header, extracted utilities, added memoization
 * - 2026-01-26: Phase 2 - Extracted data fetching logic to useReservationsData hook
 * - 2026-01-26: Phase 3 - Extracted mapping and filtering logic to useReservationsFiltering hook
 * - 2026-01-26: Phase 4 - Extracted KPI calculation logic to useReservationsKPIs hook
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReservationsTableEnhanced from '../../components/owner/ReservationsTableEnhanced.jsx';
import ReservationsMobileHeader from '../../components/owner/ReservationsMobileHeader.jsx';
import ReservationsSearchSortSheet from '../../components/owner/ReservationsSearchSortSheet.jsx';
import ReservationsListMobile from '../../components/owner/ReservationsListMobile.jsx';
import BookingDetailModal from '../../components/owner/BookingDetailModal.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { useReportingPeriod } from '../../contexts/ReportingPeriodContext.jsx';
import { SkeletonCard, SkeletonTable } from '../../components/shared/SkeletonLoader.jsx';
import ReportingPeriodBar from '../../components/shared/ReportingPeriodBar.jsx';
import OwnerModuleCard from '../../components/shared/OwnerModuleCard.jsx';
import OwnerPageChrome from '../../components/owner/OwnerPageChrome.jsx';
import KPICardRow from '../../components/shared/KPICardRow.jsx';
import { sanitizeForOwnerView } from '../../utils/sanitizeForOwnerView.js';
import { normalizeBookingForModal } from '../../utils/bookingDetailModalUtils.js';
import { useReservationsFiltering } from '../../hooks/useReservationsFiltering.js';
import { useReservationsKPIs } from '../../hooks/useReservationsKPIs.js';
import { formatReportingPeriodRange } from '../../utils/reservationsDateUtils.js';
import { formatFreshness, getPropertyShortName } from '../../utils/reservationsFormatting.js';
import { useReservationsData } from '../../hooks/useReservationsData.js';
import { useSearchParams } from 'react-router-dom';
import { getReservationDetail } from '../../services/ownerApi.js';
import { sortReservations } from '../../utils/reservationsTableSort.js';

export default function ReservationsPage({ onLogout }) {
  const { effectiveMode, selectedProperty } = useSession();
  const { isMobile } = useViewport();
  const { rangeStart, rangeEnd, getResolvedLabel, setPreset } = useReportingPeriod();
  const [searchParams, setSearchParams] = useSearchParams();
  const [periodExpanded, setPeriodExpanded] = useState(false);
  const [searchSortSheetOpen, setSearchSortSheetOpen] = useState(false);
  
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
  // Phase 2: Performance debug mode (only when ?debug_reservations_perf=1 or NODE_ENV === 'development')
  const perfDebug = typeof window !== 'undefined' && (
    new URLSearchParams(window.location.search).get('debug_reservations_perf') === '1' ||
    process.env.NODE_ENV === 'development'
  );
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailFetchError, setDetailFetchError] = useState(null);
  const [sortColumn, setSortColumn] = useState('check_in_date');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Phase 2: Render counter for performance baseline
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  // Initialize Reporting Period to current month on mount (if not already set)
  // Note: ReportingPeriodContext defaults to 'current-month', so this is just a safety check
  useEffect(() => {
    // Only set if range is not current month (avoid infinite loop)
    if (rangeStart && rangeEnd) {
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      if (rangeStart.getTime() !== currentMonthStart.getTime() || 
          rangeEnd.getTime() !== currentMonthEnd.getTime()) {
        setPreset('current-month');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Phase 2: Extract data fetching to custom hook
  const { reservations, metadata, aggregates, loading, error } = useReservationsData(
    selectedProperty?.listing_id,
    rangeStart,
    rangeEnd,
    debug
  );

  // Phase 2: Memoized handlers to prevent unnecessary child re-renders
  const handleBookingClick = useCallback((booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBooking(null);
    setDetailFetchError(null);
    if (searchParams.get('reservation_id')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('reservation_id');
        return next;
      });
    }
  }, [searchParams, setSearchParams]);

  const handleSortChange = useCallback((column) => {
    setSortColumn((prev) => {
      if (prev === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      return column;
    });
    if (column !== sortColumn) {
      setSortDirection('asc');
    }
  }, [sortColumn]);

  // Phase 3: Extract mapping and filtering to custom hook
  const { allMappedReservations, dateFilteredReservations, displayedReservations } = useReservationsFiltering(
    reservations,
    rangeStart,
    rangeEnd,
    debug
  );

  // Open modal when ?reservation_id= is in URL (e.g. from Financials Ledger Guest Name link).
  // If reservation is in displayedReservations, use it; otherwise fetch by ID so modal opens even when not in current list.
  const reservationIdFromUrl = searchParams.get('reservation_id');
  useEffect(() => {
    if (!reservationIdFromUrl) return;
    const id = parseInt(reservationIdFromUrl, 10);
    if (isNaN(id)) return;

    const inList = displayedReservations?.find((r) => Number(r.reservation_id || r.id) === id);
    if (inList) {
      setSelectedBooking(inList);
      setIsModalOpen(true);
      setDetailFetchError(null);
      return;
    }

    // Not in current list: fetch by ID so modal opens regardless of period
    let cancelled = false;
    setDetailFetchError(null);
    getReservationDetail(id)
      .then((res) => {
        if (cancelled) return;
        const reservation = res?.reservation ?? res?.data?.reservation;
        if (!reservation) {
          setDetailFetchError('Reservation not found');
          return;
        }
        const booking = {
          ...reservation,
          id: reservation.reservation_id,
          reservation_id: reservation.reservation_id,
          guest_name: reservation.guest_name,
          check_in_date: reservation.checkin_date ?? reservation.check_in_date,
          check_out_date: reservation.checkout_date ?? reservation.check_out_date,
          checkin_date: reservation.checkin_date ?? reservation.check_in_date,
          checkout_date: reservation.checkout_date ?? reservation.check_out_date,
          canonical_financial_summary: res?.canonical_financial_summary ?? res?.data?.canonical_financial_summary,
          financials_persisted_summary: res?.financials_persisted_summary ?? res?.data?.financials_persisted_summary,
        };
        setSelectedBooking(booking);
        setIsModalOpen(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailFetchError(err?.message || 'Failed to load reservation');
      });
    return () => { cancelled = true; };
  }, [reservationIdFromUrl, displayedReservations, loading]);

  // Format Reporting Period date range for header and tooltips (must be before useReservationsKPIs)
  const reportingPeriodRange = useMemo(() =>
    formatReportingPeriodRange(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  // Phase 2: Performance baseline logging (debug-only)
  useEffect(() => {
    if (perfDebug && !loading && displayedReservations) {
      const rowCount = displayedReservations.length;
      console.log('[PERF_BASELINE] ReservationsPage render:', {
        renderCount: renderCountRef.current,
        rowCount,
        timestamp: performance.now(),
      });
    }
  }, [perfDebug, loading, displayedReservations]);

  // Phase 4: Extract KPI calculations to custom hook (aggregates = authoritative totals; dateRangeLabel for tooltips)
  const { bookedReservations, inquiriesCount, cancellationsCount, periodNights, periodGross, channels, kpiCards } = useReservationsKPIs(
    dateFilteredReservations,
    displayedReservations,
    aggregates,
    reportingPeriodRange || '',
    debug
  );

  // Phase 2: Memoized derived values to prevent unnecessary prop churn
  // MUST be before any conditional returns to satisfy Rules of Hooks
  const propertyShortName = useMemo(() =>
    selectedProperty ? getPropertyShortName(selectedProperty) : 'Property',
    [selectedProperty]
  );

  // Shared width constraint for all Reservations page content (matches header card width)
  const contentWidthStyle = useMemo(() => ({
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  }), []);

  // Format freshness display (needed in render)
  const freshnessText = metadata ? formatFreshness(metadata) : '';
  const isStale = metadata?.staleness?.is_stale || false;

  // Sorted reservations for mobile list (same order as table)
  const sortedReservationsForMobile = useMemo(
    () => sortReservations(displayedReservations, sortColumn, sortDirection),
    [displayedReservations, sortColumn, sortDirection]
  );

  // Summary for mobile list footer (same semantics as table footer)
  const mobileListSummaryData = useMemo(() => ({
    rowCount: aggregates?.reservations_count ?? displayedReservations.length,
    totalNights: aggregates?.total_nights ?? displayedReservations.reduce((s, r) => s + (Number(r.nights) || 0), 0),
    totalOwnerNet: aggregates?.total_owner_net ?? displayedReservations.reduce((s, r) => {
      const n = r.financials?.ownerNet != null ? Number(r.financials.ownerNet) : (r.revenue ?? r.total_price);
      return s + (n != null && !Number.isNaN(Number(n)) ? Number(n) : 0);
    }, 0),
  }), [aggregates, displayedReservations]);

  if (loading) {
    return (
      <OwnerPageChrome
        title={selectedProperty?.name || 'Property'}
        subtitle="Reservation list and booking details"
        compactLayout={isMobile}
      >
        <SkeletonTable rows={5} />
      </OwnerPageChrome>
    );
  }

  if (error) {
    return (
      <OwnerPageChrome
        title={selectedProperty?.name || 'Property'}
        subtitle="Reservation list and booking details"
        compactLayout={isMobile}
      >
        <div className="owner-alert owner-alert-error" style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}>
          <strong>Error loading reservations</strong>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-4)', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </OwnerPageChrome>
    );
  }

  if (!selectedProperty) {
    return (
      <div className="owner-dashboard-container">
        <div className="owner-alert owner-alert-info" style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}>
          Please select a property to view reservations.
        </div>
      </div>
    );
  }

  return (
    <OwnerPageChrome
      title={propertyShortName}
      subtitle="Reservation list and booking details"
      compactLayout={isMobile}
    >
      {detailFetchError && (
        <div style={contentWidthStyle}>
          <div className="owner-alert owner-alert-error" style={{ marginBottom: 'var(--spacing-3)' }} role="alert">
            {detailFetchError}
            <button type="button" onClick={() => setDetailFetchError(null)} style={{ marginLeft: 'var(--spacing-2)', textDecoration: 'underline' }}>Dismiss</button>
          </div>
        </div>
      )}
      {/* KPI + filter always visible; only results area switches between empty / table / list */}
      {isMobile ? (
        <>
          {/* Mobile: KPI + filter header (always) + results area (empty or list) */}
          <div style={contentWidthStyle} data-reservations-kpi-mobile="true">
            <KPICardRow kpis={kpiCards} isMobile={true} data-testid="kpi-card-row" />
          </div>
          <ReservationsMobileHeader
            periodLabel={getResolvedLabel()}
            periodExpanded={periodExpanded}
            onPeriodClick={() => setPeriodExpanded((e) => !e)}
            onSearchIconClick={() => setSearchSortSheetOpen(true)}
            onSelectPreset={(id) => {
              setPreset(id);
              setPeriodExpanded(false);
            }}
          />
          <div style={{ ...contentWidthStyle, paddingTop: 'var(--spacing-2)' }}>
            {displayedReservations.length === 0 && dateFilteredReservations.length === 0 ? (
              <div
                className="owner-alert owner-alert-info"
                style={{ margin: 'var(--spacing-4) 0', padding: 'var(--spacing-4)' }}
              >
                No reservations found for this listing in the selected period.
              </div>
            ) : (
              <ReservationsListMobile
                reservations={sortedReservationsForMobile}
                onRowClick={handleBookingClick}
                summaryData={mobileListSummaryData}
              />
            )}
          </div>
          <ReservationsSearchSortSheet
            isOpen={searchSortSheetOpen}
            onClose={() => setSearchSortSheetOpen(false)}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            resultCount={searchQuery.trim() ? displayedReservations.length : null}
          />
        </>
      ) : (
        <>
          {/* Desktop: KPI + card with filter in header (always) + results area (empty or table) */}
          <div style={contentWidthStyle}>
            <KPICardRow
              kpis={kpiCards}
              data-testid="kpi-card-row"
            />
          </div>

          <div style={contentWidthStyle}>
            <OwnerModuleCard
              title="Reservations"
              style={{
                maxHeight: 'calc(100vh - var(--owner-top-nav-height, 52px) - var(--owner-sticky-gap, 12px) - 200px)',
                display: 'flex',
                flexDirection: 'column',
              }}
              headerSticky={true}
              headerStickyTop="calc(var(--owner-top-nav-height, 52px) + var(--owner-sticky-gap, 12px) + 64px)"
              headerActions={
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  flexWrap: 'wrap',
                  width: '100%',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-3)',
                    flexWrap: 'wrap',
                  }}>
                    <ReportingPeriodBar />
                    {reportingPeriodRange && (
                      <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        fontWeight: 'var(--font-weight-medium)',
                        whiteSpace: 'nowrap',
                      }}>
                        Showing: {reportingPeriodRange}
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '400px',
                    marginLeft: 'auto',
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 'var(--spacing-3)',
                      fontSize: 'var(--font-size-base)',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}>🔍</span>
                    <input
                      type="text"
                      placeholder="Search reservations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-2) var(--spacing-2) var(--spacing-2) calc(var(--spacing-3) + 24px)',
                        paddingRight: searchQuery ? 'calc(var(--spacing-3) + 32px)' : 'var(--spacing-3)',
                        border: '1px solid var(--color-border-medium)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        minHeight: '36px',
                        backgroundColor: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                      }}
                      aria-label="Search reservations"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        style={{
                          position: 'absolute',
                          right: 'var(--spacing-2)',
                          background: 'none',
                          border: 'none',
                          fontSize: 'var(--font-size-xl)',
                          color: 'var(--color-text-secondary)',
                          cursor: 'pointer',
                          padding: 'var(--spacing-1)',
                          minWidth: '32px',
                          minHeight: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              }
            >
              {displayedReservations.length === 0 && dateFilteredReservations.length === 0 ? (
                <div
                  className="owner-alert owner-alert-info"
                  style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}
                >
                  No reservations found for this listing in the selected period.
                </div>
              ) : (
                <ReservationsTableEnhanced
                  reservations={displayedReservations}
                  channels={channels}
                  properties={[]}
                  onRowClick={handleBookingClick}
                  searchQuery={searchQuery}
                  aggregates={aggregates}
                  dateRangeLabel={reportingPeriodRange}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSortChange={handleSortChange}
                />
              )}
            </OwnerModuleCard>
          </div>
        </>
      )}

      <BookingDetailModal
        isOpen={isModalOpen}
        booking={selectedBooking ? normalizeBookingForModal(sanitizeForOwnerView(selectedBooking, effectiveMode), selectedProperty?.listing_id) : null}
        onClose={handleCloseModal}
      />
    </OwnerPageChrome>
  );
}

// Styles removed - now using CSS classes from owner-ui.css
